import { useState } from 'react';
import { Plus, X, Check, ListChecks } from 'lucide-react';

/**
 * Aesthetic subtask checklist for the task form. Shape matches the
 * backend `SubTask` model: `{ title, completed }`.
 */
export function TaskSubtasksSection({ form, setForm }) {
  const [draft, setDraft] = useState('');
  const subtasks = form.subtasks || [];
  const doneCount = subtasks.filter((s) => s.completed).length;

  const add = () => {
    const title = draft.trim();
    if (!title) return;
    setForm((prev) => ({ ...prev, subtasks: [...(prev.subtasks || []), { title, completed: false }] }));
    setDraft('');
  };

  const toggle = (i) => setForm((prev) => ({
    ...prev,
    subtasks: prev.subtasks.map((s, idx) => (idx === i ? { ...s, completed: !s.completed } : s)),
  }));

  const remove = (i) => setForm((prev) => ({
    ...prev,
    subtasks: prev.subtasks.filter((_, idx) => idx !== i),
  }));

  return (
    <div data-testid="task-subtasks-section">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
          <ListChecks className="w-3 h-3 inline mr-1" />Subtasks
        </label>
        {subtasks.length > 0 && (
          <span data-testid="task-subtasks-progress" className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full" style={{ background: 'var(--pd-panel-2)', color: doneCount === subtasks.length ? 'var(--pd-green)' : 'var(--pd-subtle)' }}>
            {doneCount}/{subtasks.length}
          </span>
        )}
      </div>

      {subtasks.length > 0 && (
        <div className="rounded-[16px] overflow-hidden mb-2" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' }}>
          {subtasks.map((sub, i) => (
            <div
              key={i}
              data-testid={`task-subtask-row-${i}`}
              className="group flex items-center gap-2.5 px-3 py-2 transition-colors hover:bg-black/[0.02]"
              style={{ borderTop: i > 0 ? '1px solid var(--pd-border)' : 'none' }}
            >
              <button
                type="button"
                data-testid={`task-subtask-toggle-${i}`}
                onClick={() => toggle(i)}
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                style={{
                  border: sub.completed ? 'none' : '1.5px solid var(--pd-border-strong, var(--pd-border))',
                  background: sub.completed ? 'var(--pd-green)' : 'transparent',
                }}
                title={sub.completed ? 'Mark not done' : 'Mark done'}
              >
                {sub.completed && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </button>
              <span
                className="flex-1 min-w-0 text-[13px] truncate transition-colors"
                style={{
                  color: sub.completed ? 'var(--pd-subtle)' : 'var(--pd-text)',
                  textDecoration: sub.completed ? 'line-through' : 'none',
                }}
              >
                {sub.title}
              </span>
              <button
                type="button"
                data-testid={`task-subtask-remove-${i}`}
                onClick={() => remove(i)}
                className="p-1 rounded-lg opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                style={{ color: 'var(--pd-subtle)' }}
                title="Remove subtask"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 rounded-[16px] px-3 py-1 transition-colors focus-within:ring-1" style={{ border: '1px dashed var(--pd-border)', background: 'transparent' }}>
        <Plus className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
        <input
          data-testid="task-subtask-add-input"
          value={draft}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Break it down — add a subtask"
          className="flex-1 min-w-0 bg-transparent text-[13px] py-1.5 outline-none"
          style={{ color: 'var(--pd-text)' }}
        />
        {draft.trim() && (
          <button
            type="button"
            data-testid="task-subtask-add-button"
            onClick={add}
            className="px-2 py-1 rounded-lg text-[10px] font-semibold animate-fade-in"
            style={{ background: 'var(--pd-focus)', color: 'white' }}
          >
            Add
          </button>
        )}
      </div>
    </div>
  );
}
